// Tool Registry - Central registry for all tools in Toolverse

import { ageCalculatorTool } from "./age-calculator";
import { areaChartTool } from "./area-chart";
import { aspectRatioTool } from "./aspect-ratio";
import { barChartTool } from "./bar-chart";
import { base64Tool } from "./base64-tool";
import { blogTool } from "./blog";
import { bmiCalculatorTool } from "./bmi-calculator";
import { bmrCalculatorTool } from "./bmr-calculator";
import { bodyFatCalculatorTool } from "./body-fat-calculator";
import { boilingPointCalculatorTool } from "./boiling-point-calculator";
import { boxPlotTool } from "./box-plot";
import { bubbleChartTool } from "./bubble-chart";
import { bufferRecipeCalculatorTool } from "./buffer-recipe-calculator";
import { caseConverterTool } from "./case-converter";
import { charInspectorTool } from "./char-inspector";
import { codonUsageTool } from "./codon-usage";
import { colorConverterTool } from "./color-converter";
import { colorPaletteTool } from "./color-palette";
import { compoundInterestTool } from "./compound-interest";
import { contrastCheckerTool } from "./contrast-checker";
import { countdownTimerTool } from "./countdown-timer";
import { cronExplainerTool } from "./cron-explainer";
import { csvJsonTool } from "./csv-json";
import { dataSizeTool } from "./data-size";
import { dateDifferenceTool } from "./date-difference";
import { decisionWheelTool } from "./decision-wheel";
import { diceRollerTool } from "./dice-roller";
import { dnaMwTool } from "./dna-mw";
import { donutChartTool } from "./donut-chart";
import { f1FantasyTool } from "./f1-fantasy";
import { findReplaceTool } from "./find-replace";
import { fractionCalculatorTool } from "./fraction-calculator";
import { gForceCalculatorTool } from "./g-force-calculator";
import { gcContentTool } from "./gc-content";
import { gcSkewTool } from "./gc-skew";
import { gpaCalculatorTool } from "./gpa-calculator";
import { hashGeneratorTool } from "./hash-generator";
import { heatmapTool } from "./heatmap";
import { histogramTool } from "./histogram";
import { hplcMethodTransferTool } from "./hplc-method-transfer";
import { idealWeightCalculatorTool } from "./ideal-weight-calculator";
import { iupacRegexTool } from "./iupac-regex";
import { jsonFormatterTool } from "./json-formatter";
import { jwtDecoderTool } from "./jwt-decoder";
import { lineChartTool } from "./line-chart";
import { lineSorterTool } from "./line-sorter";
import { loanCalculatorTool } from "./loan-calculator";
import { loremIpsumTool } from "./lorem-ipsum";
import { macroCalculatorTool } from "./macro-calculator";
import { markdownTableTool } from "./markdown-table";
import { massMolarityTool } from "./mass-molarity";
import { molecularWeightTool } from "./molecular-weight";
import { morseCodeTool } from "./morse-code";
import { normalityTool } from "./normality";
import { numberBaseTool } from "./number-base";
import { numberToWordsTool } from "./number-to-words";
import { od260Tool } from "./od260";
import { passwordGeneratorTool } from "./password-generator";
import { pcrMasterMixCalculatorTool } from "./pcr-master-mix-calculator";
import { percentSolutionTool } from "./percent-solution";
import { percentageCalculatorTool } from "./percentage-calculator";
import { phredTool } from "./phred";
import { pieChartTool } from "./pie-chart";
import { ppiCalculatorTool } from "./ppi-calculator";
import { proteinTranslationTool } from "./protein-translation";
import { qrCodeGeneratorTool } from "./qr-code-generator";
import { radarChartTool } from "./radar-chart";
import { randomGeneratorTool } from "./random-generator";
import { readabilityTool } from "./readability";
import { removeDuplicateLinesTool } from "./remove-duplicate-lines";
import { reverseComplementTool } from "./reverse-complement";
import { ridgelinePlotTool } from "./ridgeline-plot";
import { romanNumeralTool } from "./roman-numeral";
import { rscuTool } from "./rscu";
import { rulerTool } from "./ruler";
import { scatterPlotTool } from "./scatter-plot";
import { signatureMakerTool } from "./signature-maker";
import { sipCalculatorTool } from "./sip-calculator";
import { slugGeneratorTool } from "./slug-generator";
import { solutionDilutionTool } from "./solution-dilution";
import { statisticsCalculatorTool } from "./statistics-calculator";
import { syllableCounterTool } from "./syllable-counter";
import { textDiffTool } from "./text-diff";
import { textRepeatTool } from "./text-repeat";
import { textReverseTool } from "./text-reverse";
import { textToBinaryTool } from "./text-to-binary";
import { timerTool } from "./timer";
import { tmCalculatorTool } from "./tm-calculator";
import { typingSpeedTestTool } from "./typing-speed-test";
import { unitConverterTool } from "./unit-converter";
import { urlEncoderTool } from "./url-encoder";
import { uuidInspectorTool } from "./uuid-inspector";
import { vennDiagramTool } from "./venn-diagram";
import { violinPlotTool } from "./violin-plot";
import { waterIntakeCalculatorTool } from "./water-intake-calculator";
import { whitespaceCleanerTool } from "./whitespace-cleaner";
import { wordCounterTool } from "./word-counter";
import { wordFrequencyTool } from "./word-frequency";
import { wordUnscramblerTool } from "./word-unscrambler";
import { youtubeThumbnailTool } from "./youtube-thumbnail";

export interface ToolPage {
  name: () => string;
  path: string;
  description: () => string;
  icon: string; // emoji
}

export interface Tool {
  id: string;
  name: () => string;
  description: () => string;
  /** Emoji string or image path (e.g., "/f1-car-side.png") */
  icon: string;
  basePath: string;
  status: "stable" | "beta";
  tags: string[];
  /** Sub-pages shown in nav when inside this tool */
  pages?: ToolPage[];
}

// Import tool metadata from each tool

// Registry of all available tools
export const tools: Tool[] = [
  f1FantasyTool,
  percentageCalculatorTool,
  timerTool,
  youtubeThumbnailTool,
  typingSpeedTestTool,
  syllableCounterTool,
  wordUnscramblerTool,
  sipCalculatorTool,
  signatureMakerTool,
  unitConverterTool,
  ageCalculatorTool,
  bmiCalculatorTool,
  passwordGeneratorTool,
  dateDifferenceTool,
  randomGeneratorTool,
  wordCounterTool,
  caseConverterTool,
  jsonFormatterTool,
  loremIpsumTool,
  base64Tool,
  urlEncoderTool,
  colorConverterTool,
  hashGeneratorTool,
  textDiffTool,
  numberToWordsTool,
  slugGeneratorTool,
  removeDuplicateLinesTool,
  textRepeatTool,
  wordFrequencyTool,
  lineSorterTool,
  textReverseTool,
  whitespaceCleanerTool,
  findReplaceTool,
  textToBinaryTool,
  morseCodeTool,
  romanNumeralTool,
  charInspectorTool,
  numberBaseTool,
  dataSizeTool,
  aspectRatioTool,
  loanCalculatorTool,
  compoundInterestTool,
  csvJsonTool,
  cronExplainerTool,
  markdownTableTool,
  jwtDecoderTool,
  readabilityTool,
  uuidInspectorTool,
  blogTool,
  solutionDilutionTool,
  massMolarityTool,
  percentSolutionTool,
  normalityTool,
  molecularWeightTool,
  reverseComplementTool,
  gcContentTool,
  phredTool,
  iupacRegexTool,
  proteinTranslationTool,
  codonUsageTool,
  rscuTool,
  gcSkewTool,
  tmCalculatorTool,
  dnaMwTool,
  od260Tool,
  barChartTool,
  lineChartTool,
  areaChartTool,
  pieChartTool,
  donutChartTool,
  scatterPlotTool,
  bubbleChartTool,
  histogramTool,
  boxPlotTool,
  heatmapTool,
  radarChartTool,
  violinPlotTool,
  ridgelinePlotTool,
  vennDiagramTool,
  rulerTool,
  bmrCalculatorTool,
  macroCalculatorTool,
  bodyFatCalculatorTool,
  idealWeightCalculatorTool,
  waterIntakeCalculatorTool,
  contrastCheckerTool,
  colorPaletteTool,
  ppiCalculatorTool,
  gpaCalculatorTool,
  fractionCalculatorTool,
  statisticsCalculatorTool,
  qrCodeGeneratorTool,
  countdownTimerTool,
  diceRollerTool,
  decisionWheelTool,
  gForceCalculatorTool,
  pcrMasterMixCalculatorTool,
  bufferRecipeCalculatorTool,
  boilingPointCalculatorTool,
  hplcMethodTransferTool,
];

// Helper functions
export function getTool(id: string): Tool | undefined {
  return tools.find((t) => t.id === id);
}

export function getToolsByStatus(status: Tool["status"]): Tool[] {
  return tools.filter((t) => t.status === status);
}

export function getToolsByTag(tag: string): Tool[] {
  return tools.filter((t) => t.tags.includes(tag));
}
